import {Dispatch, FC, SetStateAction, useContext, useEffect, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {AdminEntityPageContainer, AdminEntityPageContainerProps, AdminTable, AdminTableProps, ConfirmDeleteEntityDialog} from "@buildwitt/shapeshift-common/v2/admin";
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, FormControl, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography, useEventCallback} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {AdminInputText} from "@/pages/ManagerView/ContentCreation/AdminInputText";
import {AuthContext, IconContext, INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";
import {UploadBrightcoveVideo} from "@/pages/ManagerView/ContentCreation/UploadBrightcoveVideo";
import {palette} from "@/constants";
import {VideoUploadType} from "@/types/video";
import ImageSelector from "@/pages/ManagerView/ContentCreation/ImageSelector";
import {IDropzoneFile} from "@buildwitt/shapeshift-common/v1/fileDropzone/fileDropzoneFile";
import {AdminInputSwitch} from "@/pages/ManagerView/ContentCreation/AdminInputSwitch";
import {cohortAnnouncementValidationDoc} from "@/validationDocs/user/cohortAnnouuncementValidationDocs";
import {IAnnouncementEntity, IAnnouncementEntityPayload} from "@buildwitt/shapeshift-types/entities/Modules/IAnnoucement";
import dayjs from "dayjs";
import {IAdminUsersResponseEntry} from "@buildwitt/shapeshift-types";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import {Link} from "react-router-dom";
import {AdminHeader} from "@/pages/ManagerView/ContentCreation/AdminHeader";
import {AdminFormContainer} from "@/pages/ManagerView/ContentCreation/AdminFormContainer";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import { PageBaseContext } from "@/contexts/pageBaseContext";

const useStyles = makeStyles()((theme) => ({
	formFieldset: {
		borderRadius: "4px",
		"&.Mui-expanded": {
			margin: 0
		}
	},
	formFieldsetSummary: {
		padding: theme.spacing(0, 3),
		".MuiAccordionSummary-content": {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1)
		}
	},
	subtitle: {
		opacity: 0.7
	},
	formFieldsetDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3)
	},
	formFieldsetNumber: {
		lineHeight: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "24px",
		marginRight: theme.spacing(2),
		color: theme.palette.primary.contrastText,
		borderRadius: "100%",
		aspectRatio: "1 / 1",
		backgroundColor: theme.palette.primary.main
	},
	uploadBtn: {
		height: "56px",
		color: theme.palette.primary.main,
		"&:hover": {
			backgroundColor: "transparent"
		},
		"&.Mui-disabled": {
			borderColor: palette.white,
			opacity: 0.38
		},
		flexShrink: 0
	},
	videoUrlChip: {
		border: `1px solid ${theme.palette.primary.main}`,
		".MuiChip-deleteIcon": {
			"&, &:hover": {
				color: theme.palette.primary.main
			}
		}
	},
	addThumbnailBtn: {
		position: "absolute",
		transform: "translateY(40%) translateX(40%)",
		bottom: "0",
		right: "0",
		height: "32px",
		width: "48px",
		minWidth: "unset",
		".MuiButton-startIcon": {
			margin: 0
		}
	},
	uploadRow: {
		flexDirection: "column",
		alignItems: "flex-start",
		rowGap: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			alignItems: "flex-start"
		}
	},
	thumbnailContainer: {
		border: `1px solid ${theme.palette.grey[200]}`,
		alignSelf: "flex-start",
		padding: theme.spacing(2, 6),
		position: "relative",
		boxSizing: "border-box",
		[theme.breakpoints.up("md")]: {
			aspectRatio: "1.8"
		},
		borderRadius: "4px",
		height: "176px",
		maxWidth: "100%",
		"img": {
			width: "100%",
			height: "100%",
			objectFit: "contain",
			objectPosition: "center"
		}
	},
	thumbnailContainerLabel: {
		position: "absolute",
		top: "0",
		left: "8px",
		transform: "translateY(-50%)",
		backgroundColor: palette.greyDark,
		padding: theme.spacing(0, 0.5),
		fontSize: "12px"
	},
	uploadInput: {
		flexGrow: 1,
		marginRight: theme.spacing(2),
		".MuiFormHelperText-root": {
			color: "#F9F9F9B2"
		}
	},
	uploadInputChip: {
		"input": {
			opacity: 0
		}
	},
	expandIcon: {}
}));

interface IAnnouncementAdminPageEntry extends IAnnouncementEntity {
	enrollmentCount?: number
}

interface IProps extends Partial<AdminEntityPageContainerProps<IAnnouncementEntity, "announcement">> {
	announcementId?: number,
	className?: string
}

const template: IAnnouncementEntityPayload = {
	title: "", slug: "", content: "", audience: []
}

const StateUpdater = ({disablePublish, setPublishDisabled}: {disablePublish: boolean, setPublishDisabled: Dispatch<SetStateAction<boolean>>}) => {
	useEffect(() => {
		setPublishDisabled(disablePublish);
	}, [disablePublish, setPublishDisabled]);
	// This component is used to update the state of the parent component
	// It is a workaround for the fact that the parent component does not re-render when the state changes
	return null;
}

const headCells: AdminTableProps<"user", IAdminUsersResponseEntry>["columns"] = ([
	{
		field: "firstname",
		flex: 1,
		headerName: "First Name",
		width: 200,
		type: "string"
	},
	{
		field: "lastname",
		flex: 1,
		headerName: "Last Name",
		width: 200,
		type: "string"
	},
	{
		field: "jobTitle",
		flex: 1,
		headerName: "Job Title",
		width: 200,
		type: "string"
	},
	{
		field: "jobCategory",
		flex: 1,
		headerName: "Job Category",
		hide: true,
		type: "string"
	},
	{
		field: "email",
		flex: 1,
		hide: true,
		headerName: "Email",
		type: "string"
	}
]);


export const AdminAnnouncementPageContent: FC<IProps> = ({announcementId, ...props}) => {
	const {classes, cx} = useStyles();
	const {icon} = useContext(IconContext);
	const {jwt} = useContext(AuthContext);
	const [pageBaseValue] = useContext(PageBaseContext);
	const [uploadState, setUploadState] = useState<VideoUploadType>("idle");
	const [videoId, setVideoId] = useState<string | null>(null);
	const [autogenVideoUrl, setAutogenVideoUrl] = useState<string | null>(null);
	const [enabledAutogen, setEnabledAutogen] = useState<boolean>(true);
	const [isPublishDisabled, setPublishDisabled] = useState<boolean>(true);
	const {notify} = useContext(NotificationContext);
	const intervalRef = useRef<number | null>(null);

	const IAnnouncementEntityToIAnnouncementEntityPayload = (e: IAnnouncementEntity): IAnnouncementEntityPayload => {
		const {title, slug, content, id, createdAt, updatedAt, subtitle, videoType, videoUrl, videoThumbnailUrl, scheduledDeliveryDate, audience, moduleThumbnailOverride} = e
		const videoData = (e as any).videoData;
		const audienceType = (e as any).audienceType;
		const deliveryType = (e as any).deliveryType;

		const payload = {
			title, slug, content, id, createdAt, updatedAt, subtitle, videoData, videoType, videoUrl, videoThumbnailUrl, scheduledDeliveryDate, deliveryType, moduleThumbnailOverride, audienceType, audience: audience?.map(e => e.id)
		}
		return payload;
	}

	const fetchData = useEventCallback(async () => {
		try {
			const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/announcement/video/brightcove/query`, {
				method: "POST",
				body: JSON.stringify({
					videoId
				}),
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${jwt}`
				}
			});
			if (!res.ok) {
				return;
			}

			const data = await res.json();
			if (!data?.thumbnailUrl) {
				setAutogenVideoUrl(null)
			}
			if (data?.thumbnailUrl && autogenVideoUrl !== data?.thumbnailUrl) {
				setAutogenVideoUrl(data.thumbnailUrl);
			}
		} catch (err) {
			// just ignore errors
		}
	});

	useEffect(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (!videoId) {
			return;
		}

		// fetches video data every second
		intervalRef.current = window.setInterval(fetchData, 1000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [videoId])


	return (
		<AdminEntityPageContainer<IAnnouncementAdminPageEntry, "announcement">
			entityId={announcementId}
			entityToPayload={IAnnouncementEntityToIAnnouncementEntityPayload}
			template={template}
			baseApiUrl="/api"
			validationDoc={cohortAnnouncementValidationDoc}
			type="announcement"
			componentOverrides={{
				adminHeader: (props) =>
					<AdminHeader rootSlug="/admin/announcements" rootBreadcrumb={<><PlayCircleFilledWhiteOutlinedIcon /><Link to={"/admin/announcements"}><Typography variant="body1">Announcements</Typography></Link></>} editMode={announcementId ? "edit" : "create"} {...props} />,
				adminFormContainer: ({entity, ...containerProps}) =>
					<AdminFormContainer<IAnnouncementAdminPageEntry, "announcement"> {...containerProps} entity={entity} disablePublish={isPublishDisabled} rootSlug="/admin/announcements" componentOverrides={{deleteDialog: deleteProps => <ConfirmDeleteEntityDialog {...deleteProps} componentOverrides={{content: entity?.enrollmentCount && entity?.enrollmentCount >= 1 ? <Typography variant="body1">You will be making an assigned announcement unavailable.</Typography> : <Typography variant="body1">Do you want to delete?</Typography>}} />}} />
			}}
			{...props}
		>
			{({formData, setFormData, entity, files, setFiles, validation}) => {
				const isBrightcoveVideo = formData.videoType === "brightcove" && !!formData.videoUrl;
				const allowThumbnailUpload = uploadState !== "progress" && !enabledAutogen;
				const disableUpload = (formData.videoType !== "brightcove" && formData.videoUrl && uploadState === "idle") || uploadState === "progress";
				const inputReadOnly = isBrightcoveVideo || uploadState === "progress";
				const uploadLabel = uploadState === "progress" ? "Uploading..." : "Upload file";
				const noCurrentUploadedThumbnail = !videoId || videoId === entity?.videoUrl;
				const existingPreviewUrl = noCurrentUploadedThumbnail ? entity?.videoThumbnailUrl : null;
				const uploadedThumbnailUrl = entity?.moduleThumbnailOverride?.url ?? null;
				const disablePublish = isBrightcoveVideo && !entity.videoUrl;

				const handleUpload = (videoId: string): void => {
					setVideoId(videoId);
					setFormData(prev => ({...prev, videoType: "brightcove", videoUrl: videoId}))
				}

				const handleClear = (): void => {
					setVideoId(null);
					setFormData(prev => ({...prev, videoType: "url", videoUrl: ""}))
				}

				const handleImageChange = (files?: IDropzoneFile[]): void => {
					setFiles(prev => ({...prev, moduleThumbnailOverride: files ?? null}));
					if (!files || files.length === 0) {
						setFormData(prev => ({...prev, moduleThumbnailOverride: null}));
					}
				}

				const chipVideoUrl = formData.videoUrl ? <Chip
					className={classes.videoUrlChip}
					label={formData.videoUrl}
					onDelete={handleClear}
				/> : null

				return <>
					<StateUpdater disablePublish={disablePublish} setPublishDisabled={setPublishDisabled} />
					<Accordion defaultExpanded className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>1</span><Typography variant="h6" component="p">Announcement Information</Typography></Stack>
							<Typography className={classes.subtitle}>Add the basic details for your announcement in English.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<AdminInputText error={validation?.[0]?.title} required onChange={e => setFormData(prev => ({...prev, title: e.target.value}))} value={formData.title} label={"Announcement title"} />
							<AdminInputText error={validation?.[0]?.subtitle} multiline rows={5} label="Description" onChange={e => setFormData(prev => ({...prev, subtitle: e.target.value}))} value={formData.subtitle} />
						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>2</span>
								<Typography variant="h6" component="p">Announcement Media</Typography></Stack>
							<Typography className={classes.subtitle}>Upload your announcement video and thumbnail image.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<Stack className={classes.uploadRow}>
								<AdminInputText className={cx(classes.uploadInput, isBrightcoveVideo && classes.uploadInputChip)} error={validation?.[0]?.videoUrl} onChange={e => setFormData(prev => ({...prev, videoUrl: e.target.value, videoType: "url"}))} value={formData.videoUrl} InputProps={{readOnly: inputReadOnly, startAdornment: isBrightcoveVideo ? chipVideoUrl : null}} label={"Video"} helperText="Upload a video file (.mp4, .mov, .avi) or paste a YouTube link (copy it from the browser address bar, not the 'Share' button). Max file size: 10GB." />
								<UploadBrightcoveVideo uploadState={uploadState} setUploadState={setUploadState} handleSuccess={handleUpload} lessonId={entity?.id ?? 0}>
									{({onClick, progress, error: uploadError}) => <Stack flexShrink={0}>
										<Button variant="outlined" className={classes.uploadBtn} onClick={onClick} startIcon={<UploadFileOutlinedIcon />} disabled={disableUpload}>{uploadLabel}</Button>
										{uploadError ? <Typography color="error.main" variant="body2">{uploadError}</Typography> : null}</Stack>}
								</UploadBrightcoveVideo>
							</Stack>
							{disablePublish ? <Typography>The video is still processing. Please wait until the processing has completed to publish the announcement</Typography> : null}
							<AdminInputSwitch label="Use the auto-generated or fallback video thumbnail" onChange={(e) => setEnabledAutogen(prev => !prev)} checked={enabledAutogen} />
							<ImageSelector disabled={!allowThumbnailUpload} label="Thumbnail" defaultImageUrl={uploadedThumbnailUrl ?? existingPreviewUrl ?? autogenVideoUrl ?? undefined} onImageSelected={handleImageChange} showDeleteIcon={uploadedThumbnailUrl && allowThumbnailUpload} />
							<Box sx={{marginBottom: "20px"}} />

						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>3</span><Typography variant="h6" component="p">Announcement Audience</Typography></Stack>
							<Typography className={classes.subtitle}>Schedule when to send your announcement.</Typography>
							</AccordionSummary>
							<AccordionDetails className={classes.formFieldsetDetails}>
								{entity?.delivered && <Typography>This announcement has been delivered, audience cannot be changed.</Typography>}
								<FormControl>
									<RadioGroup
										row
										onChange={e => !entity?.delivered && setFormData(prev => ({...prev, audienceType: e.target.value}))}
										value={formData.audienceType || "all-employees"}
										aria-labelledby="audience-radio-buttons-group-label"
										name="audience-radio-buttons-group"
									>
										<FormControlLabel value="all-employees" control={<Radio />} label="All Employees" />
										<FormControlLabel value="specific-employees" control={<Radio />} label="Selected Employees" />
										{pageBaseValue.bwAdmin && <FormControlLabel value="all-users" control={<Radio />} label="All Users" />}
									</RadioGroup>
								</FormControl>

								{formData.audienceType === "specific-employees" && 
									<AdminTable<"user", IAdminUsersResponseEntry>
										url="/api/user"
										columns={headCells}
										enableDelete={false}
										type="user"
										isRowSelectable={() => !entity?.delivered}
										onSelectionModelChange={e => {
											if(entity?.delivered){
												return;
											}
											setFormData(prev => ({...prev, audience: e as number[]}))
										}}
										selectionModel={formData.audience ?? []}
										keepNonExistentRowsSelected
									/>
								}
						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>3</span><Typography variant="h6" component="p">Announcement Schedule</Typography></Stack>
							{!entity?.delivered && <Typography className={classes.subtitle}>Schedule when to send your announcement.</Typography>}
							</AccordionSummary>
							<AccordionDetails className={classes.formFieldsetDetails}>
								{/* Make a new radio below to select delivery between publish immediately and schedule */}
								{entity?.delivered 
									? <Typography>This announcement has been delivered.</Typography> 
									: <Box display={"flex"}>
										<RadioGroup
											row
											onChange={e => setFormData(prev => ({...prev, deliveryType: e.target.value}))}
											value={formData.deliveryType || "publish-immediately"}
											aria-labelledby="delivery-radio-buttons-group-label"
											name="delivery-radio-buttons-group"
										>
											<FormControlLabel value="publish-immediately" control={<Radio />} label="Publish Immediately" />
											<FormControlLabel value="schedule-delivery" control={<Radio />} label="Schedule Delivery" />
										</RadioGroup>

										{formData.deliveryType === "schedule-delivery" && 
											<LocalizationProvider dateAdapter={AdapterDayjs}>
												<DatePicker
													label="Date Due"
													value={formData.scheduledDeliveryDate ? dayjs(formData.scheduledDeliveryDate) : null}
													renderInput={(params) => <TextField sx={{height: "20px"}} InputLabelProps={{shrink: true}} {...params} />}
													onChange={e => setFormData(prev => ({...prev, scheduledDeliveryDate: e}))}

												/>
											</LocalizationProvider>
										}
									</Box>
								}
								<Box padding={.5}/>
						</AccordionDetails>
					</Accordion>
				</>
			}}
		</AdminEntityPageContainer>
	);
}
